import { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js'

import { createAuthentication } from '@redwoodjs/auth'

import {
  clearDriverToken,
  decodeDriverToken,
  getActiveDriverToken,
} from './lib/driverAuth'

const redirectUrl = process.env.AUTH0_REDIRECT_URI ?? window.location.origin

const auth0Options: Auth0ClientOptions = {
  domain: process.env.AUTH0_DOMAIN || '',
  clientId: process.env.AUTH0_CLIENT_ID || '',
  cacheLocation: 'localstorage',
  authorizationParams: {
    redirect_uri: redirectUrl,
    audience: process.env.AUTH0_AUDIENCE,
    scope: 'openid email profile read:current_user offline_access',
  },
}

const auth0 = new Auth0Client(auth0Options)

const buildDriverAwareAuth = () => {
  const authImplementation = {
    type: 'auth0',
    client: auth0,
    restoreAuthState: async () => {
      if (
        window?.location?.search?.includes('code=') &&
        window?.location?.search?.includes('state=')
      ) {
        const { appState } = await auth0.handleRedirectCallback()
        const url =
          appState && appState.targetUrl
            ? appState.targetUrl
            : window.location.pathname
        window.location.assign(url)
      }
    },
    login: async (options?: Parameters<typeof auth0.loginWithRedirect>[0]) =>
      auth0.loginWithRedirect(options),
    logout: async (options?: Parameters<typeof auth0.logout>[0]) => {
      clearDriverToken()
      return auth0.logout(options)
    },
    signup: async (options?: Parameters<typeof auth0.loginWithRedirect>[0]) =>
      auth0.loginWithRedirect({
        ...options,
        authorizationParams: {
          screen_hint: 'signup',
          prompt: 'login',
        },
      }),
    getToken: async () => {
      const driverToken = getActiveDriverToken()
      if (driverToken) return driverToken
      return auth0.getTokenSilently()
    },
    getUserMetadata: async () => {
      const driverToken = getActiveDriverToken()
      if (driverToken) return decodeDriverToken(driverToken)
      const user = await auth0.getUser()
      return user || null
    },
  }

  return createAuthentication(authImplementation)
}

export const { AuthProvider, useAuth } = buildDriverAwareAuth()
